var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { AUTOMOTIVE_THEME_PRESETS } from '../automotive/themes';
import { UI_PRESETS, resolveUiTheme, } from './uiTheme';
import { parseCssPx, resolveFontSizeTriple, toCssPxToken } from '../theme/themeUtils';
var AUTOMOTIVE = new Set(AUTOMOTIVE_THEME_PRESETS);
/** Read raw override from props without resolving. */
export function readNodeUiThemeProp(props) {
    if (props.uiTheme !== undefined)
        return props.uiTheme;
    var theme = props.theme;
    if (theme === undefined || theme === null)
        return undefined;
    if (typeof theme === 'object')
        return theme;
    if (typeof theme === 'string') {
        if (theme === 'inherit')
            return 'inherit';
        if (AUTOMOTIVE.has(theme))
            return undefined; // automotive owns this prop
        if (theme in UI_PRESETS)
            return theme;
    }
    return undefined;
}
/** Normalize a raw prop into UiThemeInput, or null for inherit. */
export function normalizeNodeUiTheme(raw) {
    if (raw === undefined || raw === 'inherit')
        return null;
    if (typeof raw === 'string') {
        if (raw in UI_PRESETS)
            return { preset: raw };
        return null;
    }
    if (typeof raw === 'object' && raw !== null)
        return raw;
    return null;
}
/**
 * Flat component typography props (highest priority).
 * `textColor` preferred; `color` is a label-style alias for text fill.
 */
export function flatTypographyFromProps(props) {
    var _a;
    if (props === void 0) { props = {}; }
    var out = {};
    var textColor = (_a = props.textColor) !== null && _a !== void 0 ? _a : props.color;
    if (textColor != null && textColor !== '')
        out.text = String(textColor);
    if (props.textMuted != null && props.textMuted !== '')
        out.textMuted = String(props.textMuted);
    var fontSize = toCssPxToken(props.fontSize);
    if (fontSize)
        out.fontSize = fontSize;
    var fontSizeSm = toCssPxToken(props.fontSizeSm);
    if (fontSizeSm)
        out.fontSizeSm = fontSizeSm;
    var fontSizeLg = toCssPxToken(props.fontSizeLg);
    if (fontSizeLg)
        out.fontSizeLg = fontSizeLg;
    return out;
}
export function hasCustomTextColor(props) {
    if (props === void 0) { props = {}; }
    if ((props.textColor != null && props.textColor !== '') ||
        (props.color != null && props.color !== '')) {
        return true;
    }
    var node = normalizeNodeUiTheme(readNodeUiThemeProp(props));
    if (node && node.text != null && String(node.text).trim() !== '')
        return true;
    return false;
}
export function hasCustomFontSize(props) {
    if (props === void 0) { props = {}; }
    if (props.fontSize != null && props.fontSize !== '')
        return true;
    var node = normalizeNodeUiTheme(readNodeUiThemeProp(props));
    if (node && node.fontSize != null && String(node.fontSize).trim() !== '')
        return true;
    return false;
}
/**
 * Effective tokens for a node: app theme ← node uiTheme ← flat typography props.
 * Component flat props win over uiTheme; uiTheme wins over app.
 */
export function resolveEffectiveUiTokens(app, props) {
    if (props === void 0) { props = {}; }
    var appTokens = app && typeof app.getResolvedTheme === 'function' ? app.getResolvedTheme() : {};
    var nodeInput = normalizeNodeUiTheme(readNodeUiThemeProp(props));
    var nodeTokens = nodeInput ? resolveUiTheme(nodeInput) : {};
    var flat = flatTypographyFromProps(props);
    return __assign(__assign(__assign({}, appTokens), nodeTokens), flat);
}
/**
 * Resolved typography for builders.
 * Priority: flat props → node uiTheme → app tokens → numeric fallbacks.
 */
export function resolveNodeTypography(app, props, fallbacks) {
    var _a, _b;
    if (props === void 0) { props = {}; }
    var tokens = resolveEffectiveUiTokens(app, props);
    var sizes = resolveFontSizeTriple(tokens, fallbacks);
    return __assign({ text: (_a = tokens.text) !== null && _a !== void 0 ? _a : fallbacks.text, textMuted: (_b = tokens.textMuted) !== null && _b !== void 0 ? _b : fallbacks.textMuted }, sizes);
}
/** True when props request a non-inherit node override (uiTheme or flat typography). */
export function hasNodeUiThemeOverride(props) {
    return (normalizeNodeUiTheme(readNodeUiThemeProp(props)) !== null ||
        Object.keys(flatTypographyFromProps(props)).length > 0);
}
/** @deprecated Use parseCssPx from theme/themeUtils */
export var parseFontPx = parseCssPx;
